import type { AnalyticsEvent, AnalyticsEventInput } from "@autopic/shared";
import { api } from "../api/client";

/**
 * Analytics is dual-sink: the backend is always the funnel source of truth
 * (POST /api/analytics/events), and PostHog is an optional client-side mirror
 * that is *fully no-op* unless VITE_POSTHOG_KEY is configured. No external
 * account is needed to run locally.
 */

type PostHogLike = {
  init: (key: string, opts: Record<string, unknown>) => void;
  capture: (event: string, props?: Record<string, unknown>) => void;
  identify: (id: string, props?: Record<string, unknown>) => void;
};

let posthog: PostHogLike | null = null;
let posthogReady = false;

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST ?? "https://eu.i.posthog.com";

/** Lazily import & init posthog-js only when a key exists. */
async function initPostHog(): Promise<void> {
  if (posthogReady || !POSTHOG_KEY) return;
  posthogReady = true;
  try {
    const mod = await import("posthog-js");
    posthog = (mod.default ?? mod) as unknown as PostHogLike;
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      capture_pageview: false,
      autocapture: false,
      disable_session_recording: true,
      persistence: "memory",
    });
  } catch {
    posthog = null; // stay silent — backend remains source of truth
  }
}

export interface AnalyticsContext {
  garageId: string;
  sessionId: string;
  sandbox: boolean;
}

let ctx: AnalyticsContext | null = null;

export function initAnalytics(context: AnalyticsContext): void {
  ctx = context;
  void initPostHog();
  if (posthog) {
    posthog.identify(context.sessionId, {
      garageId: context.garageId,
      sandbox: context.sandbox,
    });
  }
}

/**
 * Fire a funnel event. Safe to call before initAnalytics (no-ops gracefully),
 * though in practice initAnalytics runs at widget_loaded.
 */
export function track(
  event: AnalyticsEvent,
  properties?: Record<string, unknown>,
): void {
  if (!ctx) return;

  const payload: AnalyticsEventInput = {
    garageId: ctx.garageId,
    sessionId: ctx.sessionId,
    event,
    properties,
    sandbox: ctx.sandbox,
    ts: new Date().toISOString(),
  };

  void api.sendAnalytics(payload);

  if (posthog) {
    posthog.capture(`autopic_${event}`, {
      ...properties,
      garageId: ctx.garageId,
      sessionId: ctx.sessionId,
      sandbox: ctx.sandbox,
    });
  }
}
